/**
 * Placeholder page used by every stub route until its real
 * implementation is ported from `ordo-monorepo/services/portal/`,
 * AND by every empty-state branch on a multi-variant page (Billing,
 * Settings, Orders) when the user holds none of the page's variants.
 *
 * Renders the canonical `OrdoPageHeader` + the summary as body copy +
 * an optional dev-mode porting hint so the chrome (icon chip + h1 size
 * + spacing) is pixel-identical to a full page like Billing or My
 * Family. Caller is responsible for mounting Placeholder inside a
 * `<OrdoPageContainer>`.
 */

import type { LucideIcon } from 'lucide-react'

import { OrdoPageHeader } from '@/components/ordo-page-header'

interface PlaceholderProps {
  /** Display title (matches the canonical nav label). */
  title: string
  /** One-line description of what this page does. */
  summary: string
  /** Header chip icon (matches the page's sidebar nav icon). */
  icon?: LucideIcon
  /** Where in portal this page's content currently lives, for porting. */
  legacySource?: string
}

export function Placeholder({ title, summary, icon, legacySource }: PlaceholderProps) {
  return (
    <>
      <OrdoPageHeader icon={icon} title={title} />
      <p className="text-sm text-muted-foreground">{summary}</p>
      {/* Dev-only porting hint — never render this internal path to
          customers in a deployed build. */}
      {legacySource && import.meta.env.DEV ? (
        <p className="text-xs text-muted-foreground">
          <span className="font-mono opacity-70">port from:</span> <code className="font-mono">{legacySource}</code>
        </p>
      ) : null}
    </>
  )
}
